<script lang="ts">
  import type { Distributor, DistributorAccount } from "@shared/types/Distributor";
  import AddRepModalService from "@shared/svelte/Modules/AddRepModalService";
  import type { SalesRep } from "@shared/types/SalesRep";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Autocomplete from "@shared/svelte/Components/Autocomplete.svelte";
  import AddRepsManually from "../AddRepsManually.svelte";
  import Flash from "@shared/services/flashv2.js";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import SalesForceChat from "@shared/services/salesforce_chat";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import {
    showPreviewForbiddenFlash,
    isRestrictedRole,
  } from "@retailer/services/preview_forbidden_flash";
  import _ from "lodash";
  import { DistributorModalEventEntity } from "@shared/services/event_tracking/entities";
  import Footer from "./Footer.svelte";
  import ManualAccountNumberBody from "./ManualAccountNumberBody.svelte";
  import SalesRepsBody from "./SalesRepsBody.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import ChatWithSupportLink from "@shared/svelte/Components/ChatWithSupportLink.svelte";
  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  const { trackEvent } = EventTracking;
  const showAccountNumberEntry = stagedRolloutCheck(
    "connect_distributor_modal_manual_account_number",
  );
  export let submit;
  export let showConnectDistributorModal;
  export let selectedDistributor: Distributor = null;
  export let addNewSalesRep;
  export let selectedReps: any[] = [];
  export let providedDistributor: boolean = false;
  export let addSalesRepErrorCallback;
  export let submitErrorCallback;
  export let getReps = null;
  export let showDivisionDuplicateWarning = false;
  export let disableConnectWithoutReps = false;

  let showAddManualControl = false;
  let newRep: SalesRep | any = {};
  let suggestedReps: any[] = [];
  let suggestedRepsRanFor;
  let nonCSReps: any[];
  let ordersFromCustomerService: boolean = false;
  let accountNumberIsInvalid: boolean = false;
  let account: DistributorAccount = null;
  let loading: boolean = false;
  let accountSalesReps: SalesRep[] = [];
  let accountNumber: string = "";

  const service = new AddRepModalService();

  function close() {
    showConnectDistributorModal = false;
    showAddManualControl = false;
    showDivisionDuplicateWarning = false;
    accountNumber = "";
    accountNumberIsInvalid = false;
    selectedReps = [];
    resetAccount();
    if (!providedDistributor) {
      selectedDistributor = null;
    }
  }

  const getAccount = async () => {
    if (!accountNumber) {
      accountNumberIsInvalid = true;
      return;
    }
    loading = true;
    try {
      const response = await HttpPromise({
        url: `/api/retailer/distributors/${
          selectedDistributor.id
        }/distributor_accounts/${encodeURIComponent(accountNumber)}`,
        method: "GET",
      });
      loading = false;
      account = response.account;
      accountSalesReps = response.sales_reps;
      if (response.error == "taken") {
        Flash.now({
          type: "error",
          header: "Error",
          message:
            "We experienced an error retrieving your account. Please contact support for assistance.",
        });
        return;
      } else if (!account) {
        accountNumberIsInvalid = true;
        return;
      }
    } catch {
      loading = false;
    }
  };

  const resetAccount = () => {
    account = null;
    accountSalesReps = [];
  };

  const connect = async () => {
    loading = true;
    try {
      await service.connectAccount(
        selectedDistributor,
        account.account_number,
        accountSalesReps.map(sr => sr.id),
      );
      const header = "Distributor connected";
      const msg = `You can now place orders with ${selectedDistributor.name}.`;
      Flash.now({ type: "success", message: msg, timeout: 10000, header });
      DistributorsStore.pushAddedSalesReps(selectedDistributor.id, accountSalesReps);
      close();
      window["stateService"].reload();
    } catch (error) {
      Flash.now({
        type: "error",
        message: `An unexpected error occured while trying to connect ${selectedDistributor.name}.`,
      });
    } finally {
      loading = false;
    }
  };

  function submitWrapper() {
    if (isRestrictedRole(["sales_rep", "supplier"])) {
      showPreviewForbiddenFlash();
      return;
    }
    if (selectedDistributor) {
      showConnectDistributorModal = false;

      return submit(selectedDistributor, selectedReps, submitErrorCallback);
    }
  }

  function manualRep() {
    newRep = {};
    showAddManualControl = true;
  }

  function turnOffManual() {
    showAddManualControl = false;
    showDivisionDuplicateWarning = false;
  }

  async function addNewSalesRepWrapper(newRep) {
    showDivisionDuplicateWarning = false;
    let email = newRep.contact_email.split("@");
    if (email.includes("provi.com") || email.includes("getprovi.com")) {
      Flash.now({
        type: "error",
        header: "Error",
        message: "Cannot add Provi employee as sales rep",
      });
      return;
    }
    let res = await addNewSalesRep(selectedDistributor, newRep, addSalesRepErrorCallback);

    if (res && (_.get(res, "data.id") || res.id)) {
      selectedReps = [...selectedReps, res.data || res];
      showAddManualControl = false;
    } else {
      showAddManualControl = true;
    }
  }

  function getRepsCallback(res) {
    selectedReps = res;
  }

  async function getSuggestedReps() {
    if (isRestrictedRole(["sales_rep", "supplier"])) return;

    suggestedReps = await HttpPromise({
      url: `/api/retailer/distributors/${selectedDistributor.id}/suggested_reps`,
      method: "GET",
    });
    if (suggestedReps.length > 0) {
      trackEvent(
        "Connect Distributor Suggested Reps Loaded",
        new DistributorModalEventEntity({
          location: window.location.href,
          count: suggestedReps.length,
          distributorId: selectedDistributor.id,
        }),
      );
    } else {
      trackEvent(
        "Connect Distributor Suggested Reps Miss",
        new DistributorModalEventEntity({
          location: window.location.href,
          count: 0,
          distributorId: selectedDistributor?.id,
        }),
      );
    }
  }

  function runGetReps() {
    if (getReps) {
      selectedReps = [];
      getReps(selectedDistributor.id, getRepsCallback);
    }
    getSuggestedReps();
  }

  function distributorHasChanged() {
    if (selectedDistributor && selectedDistributor.id) {
      runGetReps();
    }
  }

  function addSelectedRepFromSuggested(rep: SalesRep) {
    selectedReps = [...selectedReps, rep];
    trackEvent(
      "Connect Distributor Suggested Reps Add",
      new DistributorModalEventEntity({
        location: window.location.href,
        count: selectedReps.length,
        distributorId: selectedDistributor.id,
      }),
    );
  }

  const useCustomerService = e => {
    if (e.target.checked) {
      selectedReps = [...selectedReps, { id: selectedDistributor.customer_service_rep_id }];
    } else {
      selectedReps = selectedReps.filter(
        rep => rep.id !== selectedDistributor.customer_service_rep_id,
      );
    }
  };

  const updateSelectedReps = e => {
    if (e.type == "tagRemoved") {
      selectedReps = selectedReps.filter(rep => rep.id != e.detail.id);
    } else {
      selectedReps = [...selectedReps, e.detail];
    }
  };

  const isCSRepSelected = () => {
    return nonCSReps.length != selectedReps.length;
  };

  const initializeAccountNumber = () => {
    accountNumber = selectedDistributor.account_number;
  };

  $: {
    if (selectedDistributor && selectedDistributor.id) {
      if (suggestedRepsRanFor !== selectedDistributor.id) {
        suggestedRepsRanFor = selectedDistributor.id;
        getSuggestedReps();
      }
      if (selectedDistributor.distributor_account_id) {
        initializeAccountNumber();
      }
    }
  }

  $: {
    if (selectedReps && selectedDistributor) {
      nonCSReps = selectedReps.filter(rep => rep.id != selectedDistributor.customer_service_rep_id);
      ordersFromCustomerService = isCSRepSelected();
    }
  }
</script>

<FizzStyles>
  <Modal bind:show={showConnectDistributorModal} overflowY={"auto"} closeOnBackgroundClick={false}>
    <header class="modal__header padding--small align-left">
      <div class="default-body--bold">
        Connect a Distributor
        <span class="float-right">
          <Icon type="X" height="14" width="14" extraClassNames="cursor-pointer" onPress={close} />
        </span>
      </div>
    </header>
    {#if showAddManualControl}
      <AddRepsManually
        bind:salesRep={newRep}
        distributor={selectedDistributor}
        {turnOffManual}
        {addNewSalesRepWrapper}
        bind:showDivisionDuplicateWarning
      />
    {:else}
      <div
        class="background-white connect-distributor-modal padding--medium"
        style="width: 90vw; max-width: 600px;"
      >
        {#if selectedDistributor?.id === VT_802_SPIRITS_DISTRIBUTOR_ID}
          <div class="grid-row">
            <div class="grid-col c12-12">
              <div class="default-body--bold">
                802Spirits requires a third class liquor license to send orders.
              </div>
            </div>
          </div>
        {:else}
          <div class="grid-row">
            <div class="grid-col c12-12">
              <div class="default-body--bold">
                {selectedDistributor && (selectedReps.length || providedDistributor)
                  ? "Distributor"
                  : "Select a Distributor"}
              </div>
            </div>
            <div class="grid-col c12-12 margin-top--smallest distributor-autocomplete">
              <Autocomplete
                placeholder="Search a distributor"
                objectType="distributor"
                searchUrlInput="/api/retailer/distributors/search"
                on:change={distributorHasChanged}
                disabled={!!selectedDistributor && !!(selectedReps.length || providedDistributor)}
                apiParams={{
                  with_sales_reps: true,
                  all_retailers: false,
                  hide_require_retailer_user_authentication: true,
                }}
                bind:value={selectedDistributor}
                showSimpleAutocompleteCloseIcon={false}
              />
            </div>
            {#if !selectedDistributor?.id}
              <div class="grid-row margin-top--small margin-bottom--medium">
                <div class="grid-col c12-12 align-center">
                  <div class="default-body--default align-center">
                    Missing a distributor? <ChatWithSupportLink
                      message="Let Us Know."
                      bold={false}
                      onClick={close}
                    />
                  </div>
                </div>
              </div>
            {/if}
          </div>
        {/if}

        {#if selectedDistributor?.id}
          {#if showAccountNumberEntry && selectedDistributor.requires_account_number && !selectedDistributor.is_rolling_out_integrated_orders}
            <ManualAccountNumberBody
              bind:accountNumberIsInvalid
              bind:accountNumber
              {selectedDistributor}
              {account}
              {accountSalesReps}
              closeModal={close}
            />
          {:else}
            <SalesRepsBody
              {selectedDistributor}
              {showAddManualControl}
              {nonCSReps}
              {ordersFromCustomerService}
              {useCustomerService}
              {suggestedReps}
              {selectedReps}
              {manualRep}
              {updateSelectedReps}
              {addSelectedRepFromSuggested}
              closeModal={close}
            />
          {/if}
        {/if}
      </div>
      <Footer
        isManualAccountNumberFlow={showAccountNumberEntry &&
          selectedDistributor?.requires_account_number &&
          !selectedDistributor?.is_rolling_out_integrated_orders}
        showConnectButton={!!account}
        showBackButton={!!account}
        continueButtonIsDisabled={!selectedDistributor?.id || accountNumberIsInvalid || loading}
        connectButtonIsDisabled={!selectedDistributor?.id ||
          (!selectedReps.length && disableConnectWithoutReps)}
        {getAccount}
        {submitWrapper}
        {resetAccount}
        {connect}
      />
    {/if}
  </Modal>
</FizzStyles>
